import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTestOrderById,
  fetchTestOrderResults,
} from "../../store/slices/testOrderSlice";
import api from "../../services/api";


const CLINIC_BRAND = {
  // Brand texts tuned to match the provided letterhead
  nameAm: "ወርልድ ላቦራቶሪ",
  nameEn: "World Laboratory",
  phoneAm: "ስልክ",
  phone: "0937377171/0997304040",
  email: "worldlaboratory2@gmail.com",
  footerAddressAm: "አድራሻ ፓውሎስ ሆስፒታል አቅራቢያ ከዛክ ፋርማሲ አጠገብ",
  footerAddressEn: "Address paulos hospital near kezak Pharmacy",
  footerTaglineAm:
    "አላማችን ደንበኞቻችንን በላቀ ቴክኖሎጂ እና በተመጣጣኝ ክፍያ ማገልገል ነው!!!",
};

// Use static clinic info from CLINIC_BRAND, but dynamic verified_by from Redux
const useClinicInfo = () => {
  const clinic = useSelector((state) => state.clinic.clinic);
  console.log("[useClinicInfo] clinic from Redux:", clinic);
  return {
    name: CLINIC_BRAND.nameEn,
    verified_by: clinic?.verified_by || "Senior Laboratory Scientist",
    phone: CLINIC_BRAND.phone,
    address: "",
    footerAddressAm: CLINIC_BRAND.footerAddressAm,
    footerAddressEn: CLINIC_BRAND.footerAddressEn,
    footerTaglineAm: CLINIC_BRAND.footerTaglineAm,
    nameAm: CLINIC_BRAND.nameAm,
    phoneAm: CLINIC_BRAND.phoneAm,
    email: CLINIC_BRAND.email,
    logo_url: undefined,
  };
};

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
};

const safeJsonParse = (v) => {
  if (!v) return null;
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
};

const toCleanText = (v) => {
  if (v === null || v === undefined) return "";
  return String(v).trim();
};

const sanitizeForPrint = (value) => {
  if (value === null || value === undefined) return value;
  return String(value).replace(/\u00B5/g, "\u03BC");
};

const firstNonEmptyText = (...values) => {
  for (const v of values) {
    const t = toCleanText(v);
    if (t) return t;
  }
  return "";
};

const titleCase = (value) => {
  if (!value) return "-";
  return String(value)
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const trimTrailingZeros = (value) => {
  if (value === null || value === undefined) return value;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return numeric.toString();
};

const formatRefRange = (row) => {
  const min = row?.reference_min;
  const max = row?.reference_max;
  if (min === null || min === undefined || max === null || max === undefined) return "-";
  return `${trimTrailingZeros(min)} - ${trimTrailingZeros(max)}`;
};

const formatAgeBand = (range) => {
  const minAge = range?.min_age;
  const maxAge = range?.max_age;
  const unit = range?.age_unit || "years";
  if ((minAge === null || minAge === undefined || minAge === "") && (maxAge === null || maxAge === undefined || maxAge === "")) {
    return "Any";
  }
  return `${minAge ?? "-"} - ${maxAge ?? "-"} ${unit}`;
};

const formatSex = (range) => {
  const g = String(range?.gender || "both").toLowerCase();
  const p = String(range?.pregnancy_status || "both").toLowerCase();
  if (g === "both" && p === "both") return "Any";
  if (g === "female" && p !== "both") {
    return p === "pregnant" ? "Female (Pregnant)" : "Female (Not Pregnant)";
  }
  return g === "both" ? "Any" : titleCase(g);
};

const formatTextRangeCell = (range) => {
  const textRange = toCleanText(range?.text_range);
  if (textRange) return textRange;
  const min = range?.min_value;
  const max = range?.max_value;
  if (min !== null && min !== undefined && max !== null && max !== undefined) {
    return `${trimTrailingZeros(min)} - ${trimTrailingZeros(max)}`;
  }
  return "-";
};

// Return the most likely remark text from a reference-range object
const getRangeRemark = (range) => {
  if (!range) return "";
  // Common keys: remarks, remark, note, comment, description
  return (
    firstNonEmptyText(
      range?.remarks,
      range?.remark,
      range?.note,
      range?.comment,
      range?.description,
      range?.text_range
    ) || ""
  );
};

const formatOrderAge = (order) => {
  if (!order) return "-";

  const dob = order.patient_date_of_birth || order.date_of_birth;
  if (dob) {
    const birth = new Date(dob);
    const today = new Date();
    if (!Number.isNaN(birth.getTime()) && today >= birth) {
      let years = today.getFullYear() - birth.getFullYear();
      let months = today.getMonth() - birth.getMonth();
      let days = today.getDate() - birth.getDate();

      if (days < 0) {
        months -= 1;
        const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        days += prevMonth.getDate();
      }
      if (months < 0) {
        years -= 1;
        months += 12;
      }

      if (years > 0) {
        if (years < 2 && months > 0) return `${years}y ${months}m`;
        return `${years}y`;
      }
      if (months > 0) {
        if (days > 0) return `${months}m ${days}d`;
        return `${months}m`;
      }
      return `${days}d`;
    }
  }

  const a = order.patient_age;
  if (a === null || a === undefined || a === "") return "-";
  return `${a}y`;
};

const getStatusStyle = (status) => {
  const map = {
    normal: {
      label: "Normal",
      text: "text-emerald-700",
      bg: "bg-emerald-50",
      border: "border-emerald-200",
    },
    low: {
      label: "Low",
      text: "text-amber-700",
      bg: "bg-amber-50",
      border: "border-amber-200",
    },
    high: {
      label: "High",
      text: "text-rose-700",
      bg: "bg-rose-50",
      border: "border-rose-200",
    },
    critical: {
      label: "Critical",
      text: "text-red-800",
      bg: "bg-red-50",
      border: "border-red-200",
    },
    unknown: {
      label: "N/A",
      text: "text-gray-700",
      bg: "bg-gray-50",
      border: "border-gray-200",
    },
  };
  return map[status] || map.unknown;
};

const formatResultValue = (row) => {
  if (row?.string_value !== null && row?.string_value !== undefined && String(row.string_value).trim() !== "") {
    return sanitizeForPrint(String(row.string_value));
  }
  if (row?.result_value !== null && row?.result_value !== undefined) {
    return sanitizeForPrint(trimTrailingZeros(row.result_value));
  }
  return "-";
};

const isTextParameterRow = (row) => {
  const dt = String(row?.parameter_data_type || row?.data_type || "").toLowerCase();
  if (dt === "text") return true;
  if (dt === "numeric") return false;
  if (row?.string_value !== null && row?.string_value !== undefined && String(row.string_value).trim() !== "") {
    return true;
  }
  const ranges = Array.isArray(row?.reference_ranges) ? row.reference_ranges : [];
  return ranges.some((rr) => toCleanText(rr?.text_range) !== "");
};

// Approximate number of result rows that comfortably fit on one printed page
const ROWS_PER_PAGE = 29;

const PrintDetailedReport = ({ testOrderId, multiOrderIds }) => {
  const dispatch = useDispatch();
  const { currentTestOrder, testOrderResultsById } = useSelector((s) => s.testOrders || {});
  // Fetch clinic state on mount
  useEffect(() => {
    dispatch(require("../../store/slices/clinicSlice").getClinic());
  }, [dispatch]);
  const [loading, setLoading] = useState(true);
  const [multiLoading, setMultiLoading] = useState(false);
  const [multiOrders, setMultiOrders] = useState([]);
  const [multiResultsById, setMultiResultsById] = useState({});
  const [pathologyReport, setPathologyReport] = useState(null);
  const [pathologyLoading, setPathologyLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!testOrderId) return;
      setLoading(true);
      try {
        await Promise.all([
          dispatch(fetchTestOrderById(testOrderId)).unwrap(),
          dispatch(fetchTestOrderResults(testOrderId)).unwrap(),
        ]);
      } catch (e) {
        // handled by UI
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [dispatch, testOrderId]);

  // Multi-order data loading (for combined printing)
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!multiOrderIds || multiOrderIds.length === 0) return;
      setMultiLoading(true);
      try {
        const uniqueIds = Array.from(new Set(multiOrderIds.map((id) => String(id))));
        const ordersAccumulator = [];
        const resultsAccumulator = {};

        await Promise.all(
          uniqueIds.map(async (id) => {
            try {
              const [orderResp, resultsResp] = await Promise.all([
                dispatch(fetchTestOrderById(id)).unwrap(),
                dispatch(fetchTestOrderResults(id)).unwrap(),
              ]);

              const orderRaw = orderResp?.data ?? orderResp;
              const orderNorm = orderRaw?.data ?? orderRaw;
              if (orderNorm) {
                ordersAccumulator.push(orderNorm);
              }

              const resultsRaw = resultsResp?.data ?? resultsResp;
              const resultsNorm = resultsRaw?.data ?? resultsRaw;
              if (resultsNorm) {
                const key = String(orderNorm?.id ?? id);
                resultsAccumulator[key] = resultsNorm;
              }
            } catch {
              // Ignore individual order failures; others may still succeed
            }
          })
        );

        if (!mounted) return;
        setMultiOrders(ordersAccumulator);
        setMultiResultsById(resultsAccumulator);
      } finally {
        if (mounted) setMultiLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [dispatch, multiOrderIds]);

  const order = useMemo(() => {
    const raw = currentTestOrder?.data ?? currentTestOrder ?? null;
    if (!raw) return null;
    // Avoid showing stale order if store has a different one.
    if (testOrderId && String(raw.id) !== String(testOrderId)) return null;
    return raw;
  }, [currentTestOrder, testOrderId]);

  const isMorphology = String(order?.test_testing_mode || "").toLowerCase() === "morphology";

  // const performedByForOrder = 
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!testOrderId || !isMorphology) return;
      setPathologyLoading(true);
      try {
        const res = await api.get("/pathologist-reports", {
          params: { test_order_id: testOrderId },
        });
        const data = res?.data?.data ?? res?.data;
        const first = Array.isArray(data) ? data[0] : data;
        if (mounted) setPathologyReport(first || null);
      } catch {
        if (mounted) setPathologyReport(null);
      } finally {
        if (mounted) setPathologyLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [isMorphology, testOrderId]);

  const results = useMemo(() => {
    if (!testOrderId) return null;
    return testOrderResultsById?.[String(testOrderId)] ?? null;
  }, [testOrderId, testOrderResultsById]);

  const testResultRow = results?.test_result || null;

  const parameterRows = useMemo(
    () => (Array.isArray(results?.parameter_results) ? results.parameter_results : []),
    [results?.parameter_results]
  );

  // Debug: inspect parameter rows to help diagnose missing remarks
  useEffect(() => {
    if (!parameterRows || parameterRows.length === 0) return;
    try {
      // Log a concise sample to the browser console during development
      // eslint-disable-next-line no-console
      console.debug(
        "[PrintDetailedReport] parameterRows sample:",
        parameterRows.slice(0, 10).map((r) => ({
          id: r.id,
          name: r.parameter_name,
          data_type: r.parameter_data_type || r.data_type,
          reference_ranges: Array.isArray(r.reference_ranges)
            ? r.reference_ranges.map((rr) => ({ id: rr.id, remarks: rr.remarks, text_range: rr.text_range }))
            : [],
        }))
      );
    } catch (e) {
      // ignore
    }
  }, [parameterRows]);

  const parameterPages = useMemo(() => {
    if (!parameterRows.length) return [];
    const pages = [];
    for (let i = 0; i < parameterRows.length; i += ROWS_PER_PAGE) {
      pages.push(parameterRows.slice(i, i + ROWS_PER_PAGE));
    }
    return pages;
  }, [parameterRows]);


  const performedBy = (() => {
    const legacy = Array.isArray(order?.results) ? order.results[0] : null;
    return legacy?.laboratorist_name || "Laboratory Technologist";
  })();

  // Use dynamic verified_by from clinic settings
  const { verified_by: verifiedBy, name: LAB_NAME, nameAm, email, footerAddressAm, footerAddressEn, footerTaglineAm, logo_url } = useClinicInfo();

  const overallStatus = titleCase(testResultRow?.status || order?.status || "normal"); // eslint-disable-line no-unused-vars
  const comments = testResultRow?.comments || "-";

  const pathologyData = useMemo(() => {
    const parsed = safeJsonParse(pathologyReport?.report_data);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  }, [pathologyReport]);

  const pathologyImages = useMemo(() => {
    if (!pathologyReport?.images) return [];

    // Handle both JSON string and array formats
    const parsed = safeJsonParse(pathologyReport.images);
    if (Array.isArray(parsed)) {
      return parsed.filter(img => img && String(img).trim() !== "");
    }

    // If it's already a string URL (not JSON), wrap it in an array
    if (typeof pathologyReport.images === "string" && pathologyReport.images.trim() !== "") {
      try {
        // Try parsing as JSON first
        const jsonParsed = JSON.parse(pathologyReport.images);
        return Array.isArray(jsonParsed) ? jsonParsed.filter(img => img && String(img).trim() !== "") : [];
      } catch {
        // If not JSON, treat as a single URL string
        return [pathologyReport.images];
      }
    }

    return [];
  }, [pathologyReport]);

  // Handle loading and error states for both single and multi-order modes
  if (multiOrderIds && multiOrderIds.length > 0) {
    if (multiLoading) {
      return (
        <div className="mx-auto max-w-4xl p-6">
          <div className="text-gray-700">Loading combined report…</div>
        </div>
      );
    }

    if (!multiOrders.length) {
      return (
        <div className="mx-auto max-w-4xl p-6">
          <div className="text-red-700">No orders found to print.</div>
        </div>
      );
    }
  } else {
    if (loading) {
      return (
        <div className="mx-auto max-w-4xl p-6">
          <div className="text-gray-700">Loading report…</div>
        </div>
      );
    }

    if (!order) {
      return (
        <div className="mx-auto max-w-4xl p-6">
          <div className="text-red-700">Failed to load report data.</div>
        </div>
      );
    }
  }

  // Use the first order as the primary patient context in multi-order mode
  const primaryOrder = multiOrderIds && multiOrderIds.length > 0 ? multiOrders[0] : order;

  return (
    <div className="print-page-body relative mx-auto flex min-h-screen max-w-4xl flex-col bg-white px-8 print:max-w-none print:px-6 print:py-0 print:mt-0">
      <style>{`
        .print-page-body, .print-page-body * {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif !important;
        }
        @media print {
          .print-page-body, .print-page-body * {
            font-family: Arial, Helvetica, "Noto Sans", sans-serif !important;
          }
        }
      `}</style>
      {/* Single watermark background (center logo) */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-10 print:opacity-10">
        <img
          src={logo_url || "/logo.png"}
          alt="Watermark"
          width={560}
          height={560}
          className="object-contain"
        />
      </div>

      {/* Top toolbar (hidden when printing) */}
      <div className="relative z-10 mb-4 flex items-start justify-between gap-4 print:hidden">
        <div className="text-sm text-gray-500">Print preview</div>
        <button
          type="button"
          onClick={() => window.print()}
          className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          Print
        </button>
      </div>

      {/* Letterhead wrapper */}
      <div className="relative z-10 flex flex-col print:mt-0">
        <div className="print-letterhead-header w-full bg-white pt-4 print:pt-0 print:mt-0">
          <div className="mx-auto flex w-full max-w-4xl items-center gap-4 px-4">
            {/* Logo block ~30% */}
            <div className="flex w-1/4 min-w-[25%] max-w-[30%] items-center justify-center">
              <img
                src={logo_url || "/logo.png"}
                alt="World Laboratory Logo"
                className="h-24 w-full max-w-[140px] object-contain"
              />
            </div>

            {/* Text block ~70% */}
            <div className="flex w-3/4 flex-col items-stretch justify-center text-center">
              {/* Amharic name */}
              <div
                className="w-full font-extrabold text-slate-700"
                style={{
                  fontSize: "3.0rem",
                  lineHeight: "1.5rem",
                  letterSpacing: "0.14em",
                }}
              >
                {nameAm || "ወርልድ ላቦራቶሪ"}
              </div>

              {/* English name */}
              <div
                className="mt-3 font-extrabold text-slate-700"
                style={{
                  fontSize: "2.5rem",
                  lineHeight: "2.8rem",
                  textTransform: "uppercase",
                }}
              >
                {LAB_NAME || "World Laboratory"}
              </div>

              {/* Email line under the names */}
              {email && (
                <div className="text-xs font-semibold text-gray-800">
                  <span className="italic text-gray-700">e-mail:</span> {email}
                </div>
              )}
            </div>
          </div>

          {/* Triple bottom borders like the sample image (use borders so they print reliably) */}
          <div
            className="mt-2 w-full"
            style={{
              borderTop: "6px solid #1f2937",
            }}
          />
          <div
            className="w-full"
            style={{
              borderTop: "4px solid white",
            }}
          />
          <div
            className="w-full"
            style={{
              borderTop: "6px solid #047857",
            }}
          />
        </div>
      </div>

      {/* Report Body */}
      <div className="mt-2 flex-1">
        {/* Shared patient header */}
        <div className="mt-4 flex items-start justify-between text-sm text-gray-900">
          <div className="space-y-1">
            <div>Patient Name: {primaryOrder?.patient_name || "-"}</div>
            <div>
              Patient ID: {primaryOrder?.card_number || (primaryOrder?.id ? `TO-${primaryOrder.id}` : "-")}
            </div>
          </div>
          <div className="space-y-1 text-right">
              <div>Test Date: {formatDate(primaryOrder?.ordered_at || primaryOrder?.visit_date)}</div>
              <div>
                Age / Sex: {formatOrderAge(primaryOrder)} / {titleCase(primaryOrder?.patient_gender) || "-"}
              </div>
          </div>
        </div>

        {multiOrderIds && multiOrderIds.length > 0 ? (
          <div className="mt-2 space-y-4">
            {multiOrders.map((o) => {
              const resultsForOrder = multiResultsById[String(o.id)] || null;
              const rowData = Array.isArray(resultsForOrder?.parameter_results)
                ? resultsForOrder.parameter_results
                : [];
              const onlyTextRows = rowData.length > 0 && rowData.every((r) => isTextParameterRow(r));
              const resultRow = resultsForOrder?.test_result || null;
              const commentsForOrder = resultRow?.comments || "-";

              const isMorph = String(o?.test_testing_mode || "").toLowerCase() === "morphology";
              if (isMorph) return null;

              return (
                <div
                  key={o.id}
                  className="break-inside-avoid border-t border-dashed border-gray-200 pt-1 first:border-t-0 first:pt-0"
                >
                  <div className="text-sm font-semibold text-gray-900">Test: {o.test_name || "-"}</div>

                  <div className="mt-3 overflow-hidden rounded-lg border border-gray-200">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr className="text-left text-gray-700">
                          <th className="px-4 py-1 font-semibold">Parameter</th>
                          <th className="px-4 py-1 font-semibold">Result</th>
                          <th className="px-4 py-1 font-semibold">
                            {(() => {
                              const hasSingleTextRange = rowData.some((rr) => isTextParameterRow(rr) && Array.isArray(rr?.reference_ranges) && rr.reference_ranges.length === 1);
                              return hasSingleTextRange ? "Reference Range" : "Unit";
                            })()}
                          </th>
                          {!onlyTextRows ? (
                            <>
                              <th className="px-4 py-1 font-semibold">Status</th>
                              <th className="px-4 py-1 font-semibold">Reference Range</th>
                            </>
                          ) : null}
                        </tr>
                      </thead>
                      <tbody>
                        {rowData.length ? (
                          rowData.map((r) => {
                            const textParam = isTextParameterRow(r);
                            const textRanges = Array.isArray(r?.reference_ranges) ? r.reference_ranges : [];
                            const hasSingleTextRange = textParam && textRanges.length === 1;
                            const singleTextRange = hasSingleTextRange ? textRanges[0] : null;
                            const showTextRangeTable = textParam && textRanges.length > 1;
                            console.log(`[PrintDetailedReport] Processing parameter row ID ${r.id} - textParam: ${textParam}, hasSingleTextRange: ${hasSingleTextRange}, showTextRangeTable: ${showTextRangeTable}`);
                            const style = getStatusStyle(r.status);
                            return (
                              <React.Fragment key={r.id || `${r.test_result_id}-${r.parameter_id}`}>
                                <tr
                                  className="border-t border-gray-200"
                                >
                                  <td className="px-4 text-gray-900">{r.parameter_name || "-"}</td>
                                  <td className="px-4 text-gray-900">{formatResultValue(r)}</td>
                                  <td className="px-4 text-gray-900">
                                    {hasSingleTextRange ? (
                                      <span>
                                        {getRangeRemark(singleTextRange) || "-"}
                                      </span>
                                    ) : (
                                      sanitizeForPrint(r.parameter_unit || r.unit || "-")
                                    )}
                                  </td>
                                  {!onlyTextRows ? (
                                    textParam ? (
                                      <>
                                        <td className="px-4 text-gray-500">-</td>
                                        <td className="px-4 text-gray-500">-</td>
                                      </>
                                    ) : (
                                      <>
                                        <td className="px-4 text-gray-900">
                                          <span
                                            className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${style.text} ${style.bg} ${style.border}`}
                                          >
                                            {style.label}
                                          </span>
                                        </td>
                                        <td className="px-4 text-gray-900">{formatRefRange(r)}</td>
                                      </>
                                    )
                                  ) : null}
                                </tr>
                                {showTextRangeTable ? (
                                  <tr className="border-t border-gray-100">
                                    <td className="px-4 pb-3 pt-0 text-gray-900" colSpan={onlyTextRows ? 3 : 5}>
                                      <div className="mt-2 overflow-hidden rounded-md border border-[#235F72]/25 bg-gradient-to-r from-[#235F72]/5 to-[#36F1A2]/10">
                                        <table className="w-full text-xs">
                                          <thead className="bg-[#235F72]/90 text-white">
                                            <tr className="text-left">
                                              <th className="px-3 py-2 font-semibold">Age</th>
                                              <th className="px-3 py-2 font-semibold">Sex</th>
                                              <th className="px-3 py-2 font-semibold">Range</th>
                                              <th className="px-3 py-2 font-semibold">Remark</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {textRanges.length ? (
                                              textRanges.map((rr, rrIndex) => (
                                                <tr
                                                  key={rr.id || `${r.parameter_id}-${formatSex(rr)}-${formatAgeBand(rr)}`}
                                                  className={`border-t border-[#235F72]/15 ${rrIndex % 2 === 0 ? "bg-white/70" : "bg-[#36F1A2]/10"}`}
                                                >
                                                  <td className="px-3 py-1.5 text-gray-900">{formatAgeBand(rr)}</td>
                                                  <td className="px-3 py-1.5 text-gray-900">{formatSex(rr)}</td>
                                                  <td className="px-3 py-1.5 font-semibold text-[#0B4C5F]">{formatTextRangeCell(rr)}</td>
                                                  <td className="px-3 py-1.5 text-gray-900">{getRangeRemark(rr) || "-"}</td>
                                                </tr>
                                              ))
                                            ) : (
                                              <tr className="border-t border-[#235F72]/15 bg-white/70">
                                                <td className="px-3 py-1.5 text-gray-600" colSpan={4}>No reference ranges available.</td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    </td>
                                  </tr>
                                ) : null}
                              </React.Fragment>
                            );
                          })
                        ) : (
                          <tr className="border-t border-gray-200">
                            <td className="px-4 py-3 text-gray-600" colSpan={onlyTextRows ? 3 : 5}>
                              No parameter results available.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {commentsForOrder && commentsForOrder !== "-" && (<div className="mt-4 text-sm text-gray-900">
                    <div className="font-semibold">Comments:</div>
                    <div className="mt-1 whitespace-pre-wrap">{commentsForOrder}</div>
                  </div>)}



                </div>
              );
            })}
            <div className="mt-4 grid grid-cols-1 gap-2 text-sm text-gray-900">
              <div>Performed by: {performedBy}</div>
              <div>Verified by: {verifiedBy}</div>
            </div>

          </div>
        ) : (
          <div className="mt-6">
            <div className="text-sm font-semibold text-gray-900">Test: {order.test_name || "-"}</div>

            {isMorphology ? (
              <div className="mt-3 rounded-lg border border-gray-200 p-4">
                {pathologyLoading ? (
                  <div className="text-sm text-gray-600">Loading pathology report…</div>
                ) : pathologyReport ? (
                  <div className="space-y-4 text-sm text-gray-900">
                    {(() => {
                      const clinicalHistoryText = firstNonEmptyText(pathologyData?.clinicalHistory);
                      const specimenText = firstNonEmptyText(pathologyData?.specimen);
                      const grossText = firstNonEmptyText(
                        pathologyData?.gross,
                        pathologyData?.sections?.gross_description
                      );
                      const microscopyText = firstNonEmptyText(
                        pathologyData?.microscopy,
                        pathologyData?.sections?.microscopic_description
                      );
                      const impressionText = firstNonEmptyText(
                        pathologyData?.impression,
                        pathologyData?.sections?.morphological_findings
                      );
                      const diagnosisText = firstNonEmptyText(
                        pathologyData?.diagnosis,
                        pathologyData?.sections?.diagnosis
                      );

                      const checklistItems = Array.isArray(pathologyData?.checklist)
                        ? pathologyData.checklist
                          .map((c) => toCleanText(c))
                          .filter(Boolean)
                        : [];

                      const imageSrc = toCleanText(pathologyImages?.[0]);

                      const hasAnyContent =
                        Boolean(clinicalHistoryText) ||
                        Boolean(specimenText) ||
                        Boolean(grossText) ||
                        Boolean(microscopyText) ||
                        Boolean(impressionText) ||
                        Boolean(diagnosisText) ||
                        checklistItems.length > 0 ||
                        Boolean(imageSrc);

                      if (!hasAnyContent) {
                        return (
                          <div className="text-sm text-gray-600">
                            No report content entered yet.
                          </div>
                        );
                      }

                      return (
                        <div className="relative">
                          {/* Brand watermark background */}
                          <div className="absolute inset-0 z-0 flex items-center justify-center opacity-10 print:opacity-10 pointer-events-none">
                            <img src={logo_url || "/logo.png"} alt="Brand Watermark" style={{ width: 500, height: 500, objectFit: "contain" }} />
                          </div>
                          <div className="relative z-10">
                            {/* Section: Diagnosis */}
                            <div className="my-6">
                              <div
                                className="text-xl font-bold text-white bg-green-700 py-2 px-4 rounded text-center tracking-wide"
                                style={{
                                  letterSpacing: "1px",
                                  WebkitPrintColorAdjust: "exact",
                                  printColorAdjust: "exact",
                                }}
                              >
                                DIAGNOSIS
                              </div>
                              <div className="mt-4">
                                <div className="font-bold text-lg">{diagnosisText || "PERIPHERAL MORPHOLOGY REPORT"}</div>
                                {impressionText && <div className="mt-2 font-semibold">- See description and comment</div>}
                                {comments && <div className="mt-2"><span className="font-semibold">Comment:</span><br />{comments}</div>}
                              </div>
                            </div>

                            {/* Section: Microscopic Description (with first image on the right) */}
                            <div className="my-6">
                              <div
                                className="text-xl font-bold text-white bg-green-700 py-2 px-4 rounded text-center tracking-wide"
                                style={{
                                  letterSpacing: "1px",
                                  WebkitPrintColorAdjust: "exact",
                                  printColorAdjust: "exact",
                                }}
                              >
                                MICROSCOPIC DESCRIPTION
                              </div>
                              <div className="mt-4 flex flex-col gap-4 md:flex-row morph-print-layout">
                                {/* Text column */}
                                <div className="morph-print-text flex-1 min-w-0 whitespace-pre-wrap">
                                  {microscopyText || grossText || "No microscopic description available."}
                                </div>
                                {/* First image in a square card on the right, half width on larger screens */}
                                {pathologyImages && pathologyImages.length > 0 && (
                                  <div className="morph-print-imageCol w-full md:w-1/2 flex-shrink-0">
                                    <div className="morph-print-imageCard rounded-xl border border-gray-200 bg-gray-50 p-2 shadow-sm">
                                      <div className="morph-print-imageFrame aspect-square overflow-hidden rounded-lg bg-white flex items-center justify-center">
                                        <img
                                          src={pathologyImages[0]}
                                          alt="Microscopy"
                                          className="morph-print-image h-full w-full object-cover"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Section: Specimen & Procedure Description */}
                            <div className="my-6">
                              <div
                                className="text-xl font-bold text-white bg-green-700 py-2 px-4 rounded text-center tracking-wide"
                                style={{
                                  letterSpacing: "1px",
                                  WebkitPrintColorAdjust: "exact",
                                  printColorAdjust: "exact",
                                }}
                              >
                                SPECIMEN & PROCEDURE DESCRIPTION
                              </div>
                              <div className="mt-4 whitespace-pre-wrap">
                                {specimenText || clinicalHistoryText || "No specimen/procedure description available."}
                              </div>
                            </div>

                            {/* Checklist Section (if present) */}
                            {checklistItems.length ? (
                              <div className="my-6">
                                <div
                                  className="text-xl font-bold text-white bg-green-700 py-2 px-4 rounded text-center tracking-wide"
                                  style={{
                                    letterSpacing: "1px",
                                    WebkitPrintColorAdjust: "exact",
                                    printColorAdjust: "exact",
                                  }}
                                >
                                  CHECKLIST
                                </div>
                                <ul className="mt-4 list-disc pl-5">
                                  {checklistItems.map((c, idx) => (
                                    <li key={idx} className="whitespace-pre-wrap">
                                      {c}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : null}

                            {/* All images (except first) below text and above footer */}
                            {pathologyImages && pathologyImages.length > 1 && (
                              <div className="my-6">
                                <div
                                  className="text-xl font-bold text-white bg-green-700 py-2 px-4 rounded text-center tracking-wide mb-2"
                                  style={{
                                    letterSpacing: "1px",
                                    WebkitPrintColorAdjust: "exact",
                                    printColorAdjust: "exact",
                                  }}
                                >
                                  ADDITIONAL IMAGES
                                </div>
                                <div className="flex flex-wrap justify-center gap-4 mt-4">
                                  {pathologyImages.slice(1).map((img, idx) => (
                                    <img
                                      key={idx}
                                      src={img}
                                      alt={`Morphology Additional ${idx + 2}`}
                                      className="w-full max-w-xs rounded border border-gray-200 bg-gray-50 object-contain"
                                      style={{ maxHeight: "300px", minHeight: "80px" }}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    <div className="mt-6 grid grid-cols-1 gap-2 text-sm text-gray-900">
                      <div>Pathologist: {pathologyReport.pathologist_name || "-"}</div>
                      {pathologyReport.verified_at ? (
                        <div>Verified at: {formatDateTime(pathologyReport.verified_at)}</div>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-600">No pathology report available.</div>
                )}
              </div>
            ) : (
              <div className="mt-3 overflow-hidden ">
                <div className="rounded-lg border border-gray-200">

                  {(() => {
                    const onlyTextRows =
                      parameterRows.length > 0 && parameterRows.every((r) => isTextParameterRow(r));
                    return (

                  <table className="w-full text-sm ">
                      <thead className="bg-gray-50">
                        <tr className="text-left text-gray-700">
                          <th className="px-4 py-2 font-semibold">Parameter</th>
                          <th className="px-4 py-2 font-semibold">Result</th>
                          <th className="px-4 py-2 font-semibold">
                            {(() => {
                              const hasSingleTextRange = parameterRows.some((rr) => isTextParameterRow(rr) && Array.isArray(rr?.reference_ranges) && rr.reference_ranges.length === 1);
                              return hasSingleTextRange ? "Reference Range" : "Unit";
                            })()}
                          </th>
                          {!onlyTextRows ? (
                          <>
                            <th className="px-4 py-2 font-semibold">Status</th>
                            <th className="px-4 py-2 font-semibold">Reference Range</th>
                          </>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody>
                      {parameterRows.length ? (
                        parameterPages.map((pageRows, pageIndex) =>
                          pageRows.map((r, rowIndex) => {
                            console.log("this is r", r)
                            const isLastRowOfPage =
                              rowIndex === pageRows.length - 1 &&
                              pageIndex < parameterPages.length - 1;
                            const textParam = isTextParameterRow(r);
                            const textRanges = Array.isArray(r?.reference_ranges) ? r.reference_ranges : [];
                            const hasSingleTextRange = textParam && textRanges.length === 1;
                            const singleTextRange = hasSingleTextRange ? textRanges[0] : null;
                            const showTextRangeTable = textParam && textRanges.length > 1;
                            return (
                              <React.Fragment key={r.id || `${r.test_result_id}-${r.parameter_id}`}>
                                <tr
                                  className="border-t border-gray-200"
                                  style={isLastRowOfPage ? { pageBreakAfter: "always" } : undefined}
                                >
                                  <td className="px-4 text-gray-900">{r.parameter_name || "-"}</td>
                                  <td className="px-4 text-gray-900">{formatResultValue(r)}</td>
                                  <td className="px-4 text-gray-900">
                                    {hasSingleTextRange ? (
                                      <span>
                                        {getRangeRemark(singleTextRange) || "-"}
                                      </span>
                                    ) : (
                                      sanitizeForPrint(r.parameter_unit || r.unit || "-")
                                    )}
                                  </td>
                                  {!onlyTextRows ? (
                                    textParam ? (
                                      <>
                                        <td className="px-4 text-gray-500">-</td>
                                        <td className="px-4 text-gray-500">-</td>
                                      </>
                                    ) : (
                                      <>
                                        <td className="px-4 text-gray-900">
                                          {(() => {
                                            const style = getStatusStyle(r.status);
                                            return (
                                              <span
                                                className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${style.text} ${style.bg} ${style.border}`}
                                              >
                                                {style.label}
                                              </span>
                                            );
                                          })()}
                                        </td>
                                        <td className="px-4 text-gray-900">{formatRefRange(r)}</td>
                                      </>
                                    )
                                  ) : null}
                                </tr>
                                {showTextRangeTable ? (
                                  <tr className="border-t border-gray-100">
                                    <td className="px-4 pb-3 pt-0 text-gray-900" colSpan={onlyTextRows ? 3 : 5}>
                                      <div className="mt-2 overflow-hidden rounded-md border border-[#235F72]/25 bg-gradient-to-r from-[#235F72]/5 to-[#36F1A2]/10">
                                        <table className="w-full text-xs">
                                          <thead className="bg-[#235F72]/90 text-white">
                                            <tr className="text-left">
                                              <th className="px-3 py-2 font-semibold">Age</th>
                                              <th className="px-3 py-2 font-semibold">Sex</th>
                                              <th className="px-3 py-2 font-semibold">Range</th>
                                              <th className="px-3 py-2 font-semibold">Remark</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {textRanges.length ? (
                                              textRanges.map((rr, rrIndex) => (
                                                <tr
                                                  key={rr.id || `${r.parameter_id}-${formatSex(rr)}-${formatAgeBand(rr)}`}
                                                  className={`border-t border-[#235F72]/15 ${rrIndex % 2 === 0 ? "bg-white/70" : "bg-[#36F1A2]/10"}`}
                                                >
                                                  <td className="px-3 py-1.5 text-gray-900">{formatAgeBand(rr)}</td>
                                                  <td className="px-3 py-1.5 text-gray-900">{formatSex(rr)}</td>
                                                  <td className="px-3 py-1.5 font-semibold text-[#0B4C5F]">{formatTextRangeCell(rr)}</td>
                                                            <td className="px-3 py-1.5 text-gray-900">{getRangeRemark(rr) || "-"}</td>
                                                </tr>
                                              ))
                                            ) : (
                                              <tr className="border-t border-[#235F72]/15 bg-white/70">
                                                <td className="px-3 py-1.5 text-gray-600" colSpan={4}>No reference ranges available.</td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    </td>
                                  </tr>
                                ) : null}
                              </React.Fragment>
                            );
                          })
                        )
                      ) : (
                        <tr className="border-t border-gray-200">
                          <td className="px-4 py-3 text-gray-600" colSpan={onlyTextRows ? 3 : 5}>
                            No parameter results available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                    );
                  })()}
                </div>

                {/* Comments and signatures section for standard (non-morphology) reports */}
                {comments && (
                  <div className="mt-4 text-sm text-gray-900">
                    <div className="font-semibold">Comments:</div>
                    <div className="mt-1 whitespace-pre-wrap">{comments}</div>
                  </div>
                )}

                <div className="mt-4 grid grid-cols-1 gap-2 text-sm text-gray-900">
                  <div>Performed by: {performedBy}</div>
                  <div>Verified by: {verifiedBy}</div>
                </div>
              </div>
            )}
          </div>)}

      </div>
      {/* Footer (match the provided letterhead) */}
      <div className="print-footer mt-10 pb-4 print:mt-0 print:pb-0">
        {/* <div className="h-0.5 w-full bg-green-300" />
        <div className="h-2 w-full bg-green-500" /> */}

        <div className="flex items-center justify-between gap-3 py-3">
          {/* Left spacer to balance QR width so text block stays centered */}
          <div className="hidden h-0 w-24 md:block" />

          {/* Center: footer text block, centered on the page */}
          <div className=" ml-20 text-center">
            {footerAddressAm && (
              <div className="text-sm font-semibold text-green-600">
                {footerAddressAm}
              </div>
            )}
            {footerAddressEn && (
              <div className="text-xl font-semibold text-green-500">
                {footerAddressEn}
              </div>
            )}
            {footerTaglineAm && (
              <div className="pt-1 text-xs font-semibold text-green-600">
                {footerTaglineAm}
              </div>
            )}
            {/* Phone line, matching reference style */}
            <div className="pt-1 text-sm font-semibold text-gray-900">
              ስልክ 0937377171 / 0997304040
            </div>
          </div>

          {/* Right: QR code image from public folder, at right corner */}
          <div className="flex items-center pr-1 md:pr-2">
            <img
              src="/qrworld.jpeg"
              alt="World Laboratory QR Code"
              className="h-24 w-24 object-contain print:h-24 print:w-24"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintDetailedReport;

